import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    document.title = "Terms of Service - IP Partners Inc";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-brown-100 to-brown-50 pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-brown-700 hover:text-brown-800 font-semibold mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-brown-900">Terms of Service</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-brown max-w-none space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Agreement to Terms</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                By accessing and using the IP Partners Inc website and services, you agree to be bound by these Terms 
                of Service and all applicable laws and regulations. If you do not agree with any of these terms, you 
                are prohibited from using this website and our services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Use of Website</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                You may use this website for lawful purposes only. You agree not to:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Transmit harmful, offensive, or inappropriate content</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with the proper functioning of the website</li>
                <li>Use automated systems to access the website without permission</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Legal Services and Attorney-Client Relationship</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                <strong>Consultation Requirement:</strong> An attorney-client relationship is only established through 
                a formal engagement agreement signed by both parties after an initial consultation. Using this website 
                or contacting us does not automatically create an attorney-client relationship.
              </p>
              <p className="text-brown-700 leading-relaxed mb-4">
                <strong>Scope of Services:</strong> The scope of legal services will be defined in your engagement 
                agreement. We reserve the right to decline representation if there is a conflict of interest or if 
                we determine we cannot adequately serve your legal needs.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Fees and Payment</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Legal fees and payment terms will be outlined in your engagement agreement. Unless otherwise agreed:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>Fees are based on the complexity of the matter and time required</li>
                <li>Payment is due according to the terms specified in your agreement</li>
                <li>We may require an initial retainer before beginning work</li>
                <li>You are responsible for court costs, filing fees, and other expenses</li>
                <li>Late payments may incur interest charges as permitted by law</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Intellectual Property Rights</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                All content on this website, including text, graphics, logos, images, and software, is the property 
                of IP Partners Inc or its content suppliers and is protected by copyright and intellectual property 
                laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Confidentiality</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Once an attorney-client relationship is established, we are bound by professional rules of confidentiality 
                and attorney-client privilege. However, confidential information should not be transmitted through 
                unsecured means such as general website contact forms before a relationship is established.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Disclaimer of Warranties</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                This website and its content are provided "as is" without warranties of any kind, either express or 
                implied. We do not warrant that:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>The website will be uninterrupted, timely, secure, or error-free</li>
                <li>The information provided is accurate, complete, or current</li>
                <li>Any errors or defects will be corrected</li>
                <li>The website is free from viruses or harmful components</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Limitation of Liability</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, IP Partners Inc, its attorneys, and staff shall not be liable 
                for any indirect, incidental, special, consequential, or punitive damages arising from your use of 
                this website or our services, even if we have been advised of the possibility of such damages.
              </p>
              <p className="text-brown-700 leading-relaxed mb-4">
                Our liability for any claim related to legal services is limited to the fees paid for those specific services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Termination of Services</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Either party may terminate the attorney-client relationship according to the terms specified in the 
                engagement agreement and in compliance with applicable professional rules. Upon termination, you remain 
                responsible for fees incurred up to that point.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Governing Law</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of 
                California, without regard to its conflict of law provisions. Any disputes arising from these terms 
                shall be resolved in the courts of California.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Changes to Terms</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately 
                upon posting to this website. Your continued use of the website after changes are posted constitutes 
                acceptance of the modified terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Severability</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                If any provision of these Terms of Service is found to be invalid or unenforceable, the remaining 
                provisions shall continue in full force and effect.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Contact Information</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-brown-50 p-6 rounded-lg">
                <p className="text-brown-700 mb-2"><strong>IP Partners Inc</strong></p>
                <p className="text-brown-700 mb-2">1990 N California Blvd, Walnut Creek, CA 94596, United States</p>
                <p className="text-brown-700 mb-2">Phone: +1 (833) 201-7574</p>
                <p className="text-brown-700">Email: info@ippartnerinc.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
